import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // Three states: null = checking, user object = authenticated, false = not authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Install a single axios 401 -> /auth/refresh -> retry interceptor.
  useEffect(() => {
    if (interceptorRef.current !== null) return;
    let refreshing = null;
    const id = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || '';
        const isAuthCall = url.includes('/api/auth/login')
          || url.includes('/api/auth/refresh')
          || url.includes('/api/auth/logout')
          || url.includes('/api/auth/register');
        if (status === 401 && !original._retry && !isAuthCall) {
          original._retry = true;
          try {
            refreshing = refreshing || axios.post(
              `${API_URL}/api/auth/refresh`, {}, { withCredentials: true }
            );
            await refreshing;
            refreshing = null;
            return axios({ ...original, withCredentials: true });
          } catch (refreshErr) {
            refreshing = null;
            setUser(false);
            // Replace the cryptic provider error ("No refresh token") with a
            // friendly localised message, then forward the error so that the
            // call site's `.catch` doesn't swallow it. The Spanish UI is the
            // default; toast position is global from <Toaster /> in App.
            const refreshDetail = refreshErr?.response?.data?.detail || '';
            const isMissingToken = refreshErr?.response?.status === 401
              && (refreshDetail === 'No refresh token'
                  || refreshDetail === 'Refresh token expired'
                  || refreshDetail === 'Invalid token'
                  || refreshDetail === 'User not found');
            // Suppress the "session expired" toast when the failing call is
            // the boot-time `/api/auth/me` probe — a 401 there just means
            // the visitor is anonymous, not that a session actually expired.
            const isBootAuthCheck = url.includes('/api/auth/me');
            if (isMissingToken && !isBootAuthCheck) {
              const lang = (typeof document !== 'undefined' && document.documentElement.lang) || 'es';
              toast.error(lang === 'es'
                ? 'Tu sesión expiró. Inicia sesión nuevamente.'
                : 'Your session expired. Please log in again.');
              // Keep the user on the current page so we can come back after login.
              try {
                sessionStorage.setItem('pjr:postLoginRedirect',
                  window.location.pathname + window.location.search);
              } catch { /* sessionStorage may be blocked */ }
            }
            return Promise.reject(refreshErr);
          }
        }
        return Promise.reject(error);
      }
    );
    interceptorRef.current = id;
    return () => {
      axios.interceptors.response.eject(id);
      interceptorRef.current = null;
    };
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const attempt = () => axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true, timeout: 25000 }
    );
    try {
      let response;
      try {
        response = await attempt();
      } catch (err) {
        // Cold start del backend en producción: el primer request tras un
        // deploy/reposo puede fallar con 502/timeout. Reintenta una vez
        // antes de mostrar un error (que el usuario confunde con clave mala).
        const st = err.response?.status;
        const transient = !err.response || st >= 500;
        if (!transient) throw err;
        await new Promise((r) => setTimeout(r, 2500));
        response = await attempt();
      }
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isEs = (typeof navigator !== 'undefined' && navigator.language || '').toLowerCase().startsWith('es');
      const st = error.response?.status;
      let message = formatApiErrorDetail(detail);
      if (!message) {
        if (!error.response || st >= 500) {
          message = isEs
            ? 'El servidor está iniciando. Espera unos segundos e intenta nuevamente.'
            : 'The server is starting up. Please wait a few seconds and try again.';
        } else {
          message = isEs ? 'Error al iniciar sesión' : 'Login failed';
        }
      }
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isEs = (typeof navigator !== 'undefined' && navigator.language || '').toLowerCase().startsWith('es');
      const message = formatApiErrorDetail(detail) || (isEs ? 'Error al registrarse' : 'Registration failed');
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/google/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = formatApiErrorDetail(detail) || 'Google login failed';
      return { success: false, error: message };
    }
  };

  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const isSuperAdmin = user && user.role === 'super_admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        loginWithGoogle,
        processGoogleSession,
        checkAuth,
        isAdmin,
        isSuperAdmin,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
